import { useCallback, useMemo, useState } from 'react';

/**
 * Which tab the shoot view opens on, and how that choice is remembered
 * (mock-up §04). The tab lives in localStorage per script, keyed by slug
 * because the client route only ever has a slug, and never leaves the
 * device.
 */

export type ShootTab = 'brief' | 'script' | 'questions';
/** The left-hand tab: a brief's messaging, or a script's rows when the script
 *  also carries a legacy "Interview questions" section. */
export type LeadTab = Exclude<ShootTab, 'questions'>;

type SetShootTabOptions = { remember?: boolean };

export function shootTabKey(slug: string): string {
  return `shoot-tab:${slug}`;
}

export function readStoredShootTab(slug: string): ShootTab | null {
  try {
    const raw = window.localStorage.getItem(shootTabKey(slug));
    return raw === 'brief' || raw === 'script' || raw === 'questions' ? raw : null;
  } catch {
    // Private browsing / storage disabled — the default is fine.
    return null;
  }
}

export function writeStoredShootTab(slug: string, tab: ShootTab): void {
  try {
    window.localStorage.setItem(shootTabKey(slug), tab);
  } catch {
    // Same again: remembering the tab is a nicety, never a requirement.
  }
}

/**
 * The messaging is what you read before the questions, so a first visit to a
 * brief with chosen fields opens on Brief (a script with questions opens on
 * Script the same way). A remembered tab wins, except when it points at a
 * Questions tab that has nothing in it: landing a returning visitor on an
 * empty state is worse than landing them on the brief.
 */
export function initialShootTab(
  stored: ShootTab | null,
  hasFields: boolean,
  hasQuestions: boolean,
  lead: LeadTab = 'brief',
): ShootTab {
  if (!hasFields) return 'questions';
  if (stored === 'questions' && !hasQuestions) return lead;
  return stored === 'questions' || stored === lead ? stored : lead;
}

export function useShootTab(
  slug: string,
  hasFields: boolean,
  hasQuestions: boolean,
  lead: LeadTab = 'brief',
): [ShootTab, (tab: ShootTab, options?: SetShootTabOptions) => void] {
  // The script arrives after the first render, so the default is derived on
  // every change of the inputs rather than frozen in initial state; an
  // explicit choice this session overrides it from then on.
  const fallback = useMemo(
    () => initialShootTab(slug ? readStoredShootTab(slug) : null, hasFields, hasQuestions, lead),
    [slug, hasFields, hasQuestions, lead],
  );
  const [chosen, setChosen] = useState<{ slug: string; tab: ShootTab } | null>(null);

  const setTab = useCallback(
    (tab: ShootTab, { remember = true }: SetShootTabOptions = {}) => {
      setChosen({ slug, tab });
      if (remember && slug) writeStoredShootTab(slug, tab);
    },
    [slug],
  );

  // With no chosen fields there is no strip, so the page is today's questions.
  const tab = hasFields ? (chosen?.slug === slug ? chosen.tab : fallback) : 'questions';
  return [tab, setTab];
}
